// How a run ended (ADR 0039 §4), in plain words for the chat, the runs lists and the Harness tab. Never one
// "done": a done run says whether it was checked.

export const OUTCOMES = [
  'verified_done',
  'done_unverified',
  'budget_exhausted',
  'blocked_by_policy',
  'needs_human',
  'tool_failed',
  'cancelled',
  'error',
] as const;
export type OutcomeName = (typeof OUTCOMES)[number];

export const OUTCOME_LABEL: Record<OutcomeName, string> = {
  verified_done: 'Done, verified',
  done_unverified: 'Done, not checked',
  budget_exhausted: 'Stopped at a budget',
  blocked_by_policy: 'Blocked by a rule',
  needs_human: 'Needs a person',
  tool_failed: 'A tool failed',
  cancelled: 'Stopped',
  error: 'Failed',
};

export const OUTCOME_HELP: Record<OutcomeName, string> = {
  verified_done: 'The agent answered and every check you set passed.',
  done_unverified: 'The agent answered; nothing was set up to check the answer.',
  budget_exhausted: 'A budget (steps, tool calls, time or tokens) ran out before the agent answered.',
  blocked_by_policy: 'A safeguard refused the answer, or a rule blocked the call the agent needed.',
  needs_human: 'A tool call waits for approval, or a check failed and the answer needs review.',
  tool_failed: 'The agent ended on a failing tool call.',
  cancelled: 'The person or the app stopped the run.',
  error: 'The model or the platform failed (no tool involved).',
};

const BUDGET_LABEL: Record<string, string> = {
  turns: 'steps',
  tool_calls: 'tool calls',
  tokens: 'tokens',
  time_ms: 'time',
  cost_usd: 'cost',
};

/**
 * What kind of failure a run that did not end well was (the guide's §40), from its outcome and what stopped it; the
 * server's `failureClassOf` without the tool errors (a failed tool reads as a tool failure). Null for a good end.
 */
export function failureText(
  outcome: string,
  stop?: { budget?: string; check?: string } | null,
): string | null {
  if (outcome === 'budget_exhausted') return 'budget failure';
  if (outcome === 'blocked_by_policy') return 'authorization failure';
  if (outcome === 'needs_human') return stop?.check ? 'verification failure' : null;
  if (outcome === 'tool_failed') return 'tool failure';
  if (outcome === 'error') return 'environment failure';
  return null;
}

/** The outcome in plain words, with the budget or check that stopped it. */
export function outcomeText(outcome: string, stop?: { budget?: string; check?: string } | null): string {
  const label = OUTCOME_LABEL[outcome as OutcomeName] ?? outcome;
  if (outcome === 'budget_exhausted' && stop?.budget)
    return `${label}: ${BUDGET_LABEL[stop.budget] ?? stop.budget}`;
  if (outcome === 'needs_human' && stop?.check) return `${label}: check “${stop.check}” failed`;
  return label;
}

/** The tone of an outcome badge. */
export const outcomeTone = (outcome: string): 'good' | 'neutral' | 'warn' | 'bad' =>
  outcome === 'verified_done'
    ? 'good'
    : outcome === 'done_unverified' || outcome === 'cancelled'
      ? 'neutral'
      : outcome === 'error' || outcome === 'tool_failed'
        ? 'bad'
        : 'warn';

export const TONE_CLASS: Record<ReturnType<typeof outcomeTone>, string> = {
  good: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  neutral: 'bg-card-border/40 text-page-muted',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};
